import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { IconArrowRight, IconBrandPaypal, IconBuildingBank, IconCheck, IconCopy, IconCreditCard, IconX } from "@tabler/icons-react"
import type { IChapterGivingAccount } from "@dc-ireland/shared"
import {
  accountReference,
  bankDetailsRequestMailto,
  hasBankDetails,
  hasPaypalUrl,
  hasSumupUrl,
} from "../../lib/giveConfig"
import { chapterPath, getChapterMeta } from "../../lib/chapters"

type Props = {
  open: boolean
  onClose: () => void
  account: IChapterGivingAccount | null
}

type DetailRow = { label: string; value: string; example?: boolean }

const detailRows = (account: IChapterGivingAccount): DetailRow[] => {
  const reference = accountReference(account)
  return [
    account.bankName ? { label: "Bank Name", value: account.bankName } : null,
    account.accountName ? { label: "Account Name", value: account.accountName } : null,
    account.accountNumber ? { label: "Account Number", value: account.accountNumber } : null,
    account.iban ? { label: "IBAN", value: account.iban } : null,
    account.bic ? { label: "BIC / SWIFT", value: account.bic } : null,
    reference ? { label: "Reference (example)", value: reference, example: true } : null,
  ].filter((row): row is DetailRow => Boolean(row))
}

const GiveChapterDetailsModal: React.FC<Props> = ({ open, onClose, account }) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  const handleClose = useCallback(() => {
    onClose()
    setCopiedKey(null)
  }, [onClose])

  useEffect(() => {
    if (!open) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }

    document.addEventListener("keydown", handleEscape)
    document.body.style.overflow = "hidden"

    return () => {
      document.removeEventListener("keydown", handleEscape)
      document.body.style.overflow = ""
    }
  }, [open, handleClose])

  if (!open || !account) return null

  const chapter = getChapterMeta(account.chapterSlug)
  const chapterName = chapter?.name ?? account.label
  const bankReady = hasBankDetails(account)
  const paypalReady = hasPaypalUrl(account)
  const sumupReady = hasSumupUrl(account)
  const rows = detailRows(account)

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKey(key)
      window.setTimeout(() => setCopiedKey(null), 2000)
    } catch {
      setCopiedKey(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="give-chapter-modal-title"
      onClick={handleClose}
    >
      <div
        className="relative max-h-[92vh] w-full max-w-[520px] overflow-y-auto rounded-t-2xl bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex size-9 items-center justify-center rounded-full text-[#4a454f] transition-colors hover:bg-[#f5f5f5]"
        >
          <IconX size={20} stroke={1.5} />
        </button>

        <div className="flex flex-col gap-8 px-6 pb-8 pt-11 sm:px-8">
          <div className="pr-8 text-center">
            <p className="font-dm-sans text-xs font-semibold uppercase tracking-[0.18em] text-[#9e7e43]">
              Give to
            </p>
            <h2
              id="give-chapter-modal-title"
              className="mt-2 font-outfit text-2xl font-semibold tracking-tight text-[#1f1f1f]"
            >
              {chapterName}
            </h2>
          </div>

          {sumupReady && account.sumupUrl && (
            <section className="flex flex-col gap-4">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f4f7ff] text-dc-primary-900">
                  <IconCreditCard size={18} stroke={1.5} />
                </div>
                <h3 className="font-outfit text-lg font-semibold tracking-tight text-[#1f1f1f]">
                  SumUp
                </h3>
              </div>

              <div className="flex flex-col gap-4 rounded-xl border border-[#ebebeb] px-4 py-4">
                <p className="font-dm-sans text-sm leading-6 text-[#5a5a5a]">
                  Support {chapterName} securely by card with SumUp.
                </p>
                <a
                  href={account.sumupUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit items-center gap-1.5 font-dm-sans text-sm font-semibold text-dc-primary-900 transition hover:gap-2.5"
                >
                  Continue to SumUp
                  <IconArrowRight size={16} stroke={2} />
                </a>
              </div>
            </section>
          )}

          <section className="flex flex-col gap-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f4f7ff] text-[#003087]">
                <IconBrandPaypal size={18} stroke={1.5} />
              </div>
              <h3 className="font-outfit text-lg font-semibold tracking-tight text-[#1f1f1f]">
                PayPal
              </h3>
            </div>

            {paypalReady && account.paypalUrl ? (
              <div className="flex flex-col gap-4 rounded-xl border border-[#ebebeb] px-4 py-4">
                <p className="font-dm-sans text-sm leading-6 text-[#5a5a5a]">
                  Support {chapterName} securely with PayPal.
                </p>
                <a
                  href={account.paypalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex w-fit items-center gap-1.5 font-dm-sans text-sm font-semibold text-dc-primary-900 transition hover:gap-2.5"
                >
                  Continue to PayPal
                  <IconArrowRight size={16} stroke={2} />
                </a>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-[#ebebeb] px-4 py-5">
                <p className="font-dm-sans text-sm leading-6 text-[#5a5a5a]">
                  PayPal for {chapterName} is not listed yet.
                </p>
                <a
                  href={bankDetailsRequestMailto(chapterName)}
                  className="mt-3 inline-flex font-dm-sans text-sm font-semibold text-dc-primary-900 underline decoration-from-font"
                >
                  Request PayPal link
                </a>
              </div>
            )}
          </section>

          <section className="flex flex-col gap-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#f4f7ff] text-dc-primary-900">
                <IconBuildingBank size={18} stroke={1.5} />
              </div>
              <h3 className="font-outfit text-lg font-semibold tracking-tight text-[#1f1f1f]">
                Bank transfer
              </h3>
            </div>

            {bankReady ? (
              <div className="flex flex-col gap-4 rounded-xl border border-[#ebebeb] px-4 py-4">
                <div className="flex flex-col gap-3">
                  {rows.map(({ label, value, example }) => (
                    <div
                      key={label}
                      className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3"
                    >
                      <span
                        className={`shrink-0 font-dm-sans text-sm sm:w-36 ${
                          example ? "text-[#9a9a9a]" : "text-[#5a5a5a]"
                        }`}
                      >
                        {label}
                      </span>
                      <span
                        className={`break-all font-dm-sans text-sm ${
                          example ? "font-normal text-[#9a9a9a]" : "font-medium text-[#1f1f1f]"
                        }`}
                      >
                        {value}
                      </span>
                      {!example && (
                        <button
                          type="button"
                          onClick={() => copyToClipboard(value, label)}
                          className="inline-flex shrink-0 items-center justify-center rounded-lg px-2 py-1 font-dm-sans text-sm tracking-wide text-[#003699] transition hover:bg-[#003699]/5"
                          aria-label={`Copy ${label}`}
                        >
                          {copiedKey === label ? (
                            <IconCheck size={14} stroke={2} />
                          ) : (
                            <IconCopy size={14} stroke={1.5} />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-[#ebebeb] px-4 py-5">
                <p className="font-dm-sans text-sm leading-6 text-[#5a5a5a]">
                  Bank details for {chapterName} are not listed yet.
                </p>
                <a
                  href={bankDetailsRequestMailto(chapterName)}
                  className="mt-3 inline-flex font-dm-sans text-sm font-semibold text-dc-primary-900 underline decoration-from-font"
                >
                  Request bank details
                </a>
              </div>
            )}
          </section>

          {chapter && (
            <Link
              to={chapterPath(chapter.slug)}
              className="text-center font-dm-sans text-sm font-semibold text-dc-primary-900 transition hover:underline"
              onClick={handleClose}
            >
              Visit {chapterName} page
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

export default GiveChapterDetailsModal
